import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {ChecklistNode} from "../model/checklist-node";
import {CdkDragDrop, moveItemInArray, transferArrayItem} from "@angular/cdk/drag-drop";
import {ChecklistNodeCheckpoint} from "../model/checklist-node-checkpoint";
import {ChecklistsService} from "../../../../common/services/checklists.service";
import {forkJoin, Observable} from "rxjs";
import {concatMap, filter, take} from "rxjs/operators";
import {Localization} from "../../../../common/enums/localization";
import {
  AddUpdateCheckpointModalComponent
} from "../../components/add-update-checkpoint-modal/add-update-checkpoint-modal.component";
import {MDBModalService} from "ng-uikit-pro-standard";
import {AddUpdateNodeModalComponent} from "../../components/add-update-node-modal/add-update-node-modal.component";
import {ConfirmationDialogModel, DialogService} from "@npm.chapps.com/chapps.common.angular.library";
import {TranslateService} from "@ngx-translate/core";
import {
  DuplicateMultipleTimesData,
  DuplicateMultipleTimesModalComponent
} from "../../components/duplicate-multiple-times-modal/duplicate-multiple-times-modal.component";
import {AddCheckpointType} from "../../../../common/enums/enums";
import {
  SelectCheckpointCreationMethodModal
} from "../../components/select-checkpoint-creation-method-modal/select-checkpoint-creation-method-modal.component";
import {
  SelectExistingCheckpointData,
  SelectExistingCheckpointModalComponent
} from "../../components/select-existing-checkpoint-modal/select-existing-checkpoint-modal.component";
import {TemplateCheckpoint} from "../../../../common/model/template-checkpoint";
import {flattenChecklistNode} from "../checklist.component";
import {MatDialog} from '@angular/material/dialog';

export const CHECKPOINT_DROPLIST_ID = 'CHECKPOINTS_FOR_NODE_';
export const NODE_DROPLIST_ID = 'CHILDREN_FOR_NODE_';

@Component({
  selector: 'app-checklist-node',
  templateUrl: './checklist-node.component.html',
  styleUrls: ['./checklist-node.component.scss']
})
export class ChecklistNodeComponent implements OnInit {

  @Input()
  public node: ChecklistNode;

  @Input()
  public depth = 0;

  @Input()
  public allDroplists: number[];

  @Input()
  public buildingId: number;

  @Input()
  public readOnly = false;

  /**
   * Will emit whenever changes in the node have been
   * made that cannot be reflected ad-hoc but that
   * require a complete refresh of the checklist.
   */
  @Output()
  public readonly onBecameStale = new EventEmitter<void>();

  @Output()
  public readonly onDeleted = new EventEmitter<ChecklistNode>();

  @Output()
  public  readonly onDuplicated = new EventEmitter<ChecklistNode>();

  public get allCheckpointDropLists() {
    return this.allDroplists.map(id => `${CHECKPOINT_DROPLIST_ID}${id}`);
  }

  public get allNodeDropLists() {
    return this.allDroplists.map(id => `${NODE_DROPLIST_ID}${id}`);
  }

  constructor(
      private readonly checklists: ChecklistsService,
      private readonly dialogService: MDBModalService,
      private readonly dialogs: DialogService,
      private readonly matDialog: MatDialog,
      private readonly translate: TranslateService,
  ) { }

  public get checkpointDropListId(): string {
    return `${CHECKPOINT_DROPLIST_ID}${this.node.id}`;
  }

  public get nodeDropListId(): string {
    return `${NODE_DROPLIST_ID}${this.node.id}`;
  }

  ngOnInit(): void {
  }

  public expand($event: MouseEvent, node: ChecklistNode){
    node.expanded = !node.expanded;
  }

  public async didReceive(dropping: DroppedCheckpoint) {
    await this.migrateCheckpoints(dropping).toPromise();
  }

  public async didDropNode(dropping: DroppedChecklistNode) {
    await this.migrateNodes(dropping).toPromise();
  }


  // @Todo Make these two methods generic.
  private migrateCheckpoints(dropping: DroppedCheckpoint) {
    const sourceNode = dropping.previousContainer.data;
    const targetNode = dropping.container.data;
    const targetList = [...targetNode.checkPoints];
    const sourceList = sourceNode.checkPoints;
    let persistOperation: Observable<unknown>;

    if (sourceNode !== targetNode) {
      transferArrayItem(sourceList, targetList, dropping.previousIndex, dropping.currentIndex);
      const subject = dropping.item.data;
      subject.templateChecklistNodeId = targetNode.id;
      persistOperation = this.checklists.updateChecklistNodeCheckpoint(subject).pipe(
          concatMap(() => forkJoin([
            this.checklists.sortCheckpoints(targetNode.id, targetList.map(({id}) => id)),
            this.checklists.sortCheckpoints(sourceNode.id, sourceList.map(({id}) => id)),
          ]))
      );
    } else {
      moveItemInArray(targetList, dropping.previousIndex, dropping.currentIndex);
      persistOperation =
          this.checklists.sortCheckpoints(targetNode.id, targetList.map(({id}) => id));
    }
    targetNode.checkPoints = targetList;

    return persistOperation;
  }
  private migrateNodes(dropping: DroppedChecklistNode) {
    const sourceNode = dropping.previousContainer.data;
    const targetNode = dropping.container.data;
    const targetList = [...targetNode.childNodes];
    const sourceList = sourceNode.childNodes;
    let persistOperation: Observable<unknown>;

    if (sourceNode !== targetNode) {
      transferArrayItem(sourceList, targetList, dropping.previousIndex, dropping.currentIndex);
      const subject = dropping.item.data;
      subject.parentId = targetNode.id;
      persistOperation = this.checklists.updateNode(subject, false).pipe(
          concatMap(() => forkJoin([
            this.checklists.sortNodes(targetNode.id, targetList.map(({id}) => id)),
            this.checklists.sortNodes(sourceNode.id, sourceList.map(({id}) => id)),
          ]))
      );
    } else {
      moveItemInArray(targetList, dropping.previousIndex, dropping.currentIndex);
      persistOperation =
          this.checklists.sortNodes(targetNode.id, targetList.map(({id}) => id));
    }
    targetNode.childNodes = targetList;

    return persistOperation;
  }

  protected readonly console = console;

  public didRemoveCheckpoint(checkpoint: ChecklistNodeCheckpoint) {
   console.log('Removed', checkpoint);
  }

  public removeCheckpoint(checkpoint: ChecklistNodeCheckpoint) {
    this.node.checkPoints = this.node.checkPoints.filter(({id}) => id !== checkpoint.id);
  }

  /**
   * Updating the list of Checkpoints is not possible as
   * the API returns a Checkpoint instead of a full
   * ChecklistNodeCheckpoint. Instead we'll emit
   * that the data model became stale and it's
   * up to the root component to refresh the
   * entire Checklist from it's API endpoint.
   */
  public didDuplicateCheckpoint([duplicate, original]: [ChecklistNodeCheckpoint, ChecklistNodeCheckpoint]) {
    this.onBecameStale.emit();
  }

    protected readonly Localization = Localization;

  public async addNewGroup($event: MouseEvent) {
    const createdNode = await this.presentNodeDialog(true);
    if (createdNode) {
      this.node.childNodes = [
          ...this.node.childNodes,
          createdNode,
      ];
    }
  }

  private async presentNodeDialog(create: boolean) {
    const ref = this.dialogService.show(AddUpdateNodeModalComponent, {class: 'modal-dialog-centered'})
    const createDialog = ref.content as AddUpdateNodeModalComponent;
    return await (create ? createDialog.createFor(this.node, true) : createDialog.show(this.node, true, true));
  }

  public async deleteGroup($event: MouseEvent) {
    if (await this.confirmDeletion()) {
      await this.checklists.deleteNode(this.node.id).toPromise();
      this.onDeleted.emit(this.node);
    }
  }

  private confirmDeletion() {
    return this.dialogs.confirm(ConfirmationDialogModel.create({
      title: 'delete',
      text: this.translate.instant(Localization.AreYouSureYouWantToDelete, {value: this.node.label}),
      confirmText: Localization.Delete,
    })).pipe(take(1)).toPromise();
  }

  public didDelete(node: ChecklistNode) {
    this.node.childNodes = this.node.childNodes.filter(
        ({id}) => id !== node.id
    );
  }

  public async duplicateGroup($event: MouseEvent) {
    const duplicate = await this.checklists.duplicateNode(this.node.id, this.node.parentId).toPromise();
    this.onDuplicated.emit(duplicate);
  }

  public didDuplicateNode(duplicate: ChecklistNode) {
    this.node.childNodes = [
        ...this.node.childNodes,
        duplicate,
    ];
  }

  public async addNewCheckpoint($event: MouseEvent) {
    const selectedMethod = await this.selectCreationMethod();

    switch (selectedMethod) {
      case AddCheckpointType.NEW:
        await this.createNewCheckpoint();
        break;
      default:
        await this.copyExistingCheckpoint(selectedMethod);
    }


  }

  private async selectCreationMethod() {
    const ref = this.dialogService.show(SelectCheckpointCreationMethodModal, {class: 'modal-dialog-centered'})
    const createDialog = ref.content as SelectCheckpointCreationMethodModal;
    return await createDialog.selection;
  }

  public async editGroup($event: MouseEvent) {
    await this.presentNodeDialog(false);
  }

  public makeMultipleCopies($event: MouseEvent) {
    const data: DuplicateMultipleTimesData = { original: this.node };
    const dialog = this.matDialog.open(
      DuplicateMultipleTimesModalComponent, { panelClass: 'full-bleed', data  }
    );
    dialog.afterClosed().pipe(
        take(1), filter(Boolean)
    ).subscribe(() => this.onBecameStale.emit());
  }


  private allCheckpointsIn(node: ChecklistNode) {
    const checkpoints = flattenChecklistNode(node)
        .reduce((checkpoints, node) => ([
          ...checkpoints,
          ...node.checkPoints,
        ]), [] as ChecklistNodeCheckpoint[]);
    return Promise.resolve(checkpoints);
  }

  private get predefinedCheckpoints() {
    return this.checklists.predefinedCheckpoints(this.buildingId);
  }

  private async createNewCheckpoint() {
    const ref = this.dialogService.show(AddUpdateCheckpointModalComponent, {class: 'modal-dialog-centered'})
    const createDialog = ref.content as AddUpdateCheckpointModalComponent;
    await createDialog.show(this.node.id);
  }

  private async copyExistingCheckpoint(checkpointType: AddCheckpointType.PREVIOUSLY | AddCheckpointType.PREDEFINED) {
    const checkpoints = checkpointType === AddCheckpointType.PREDEFINED ?
        this.predefinedCheckpoints : this.allCheckpointsIn(this.node);

    const data: SelectExistingCheckpointData = {
      title: Localization.YourPreviouslyAddedCheckpoints,
      checkpoints$: checkpoints,
    }

    const ref = this.dialogService.show(SelectExistingCheckpointModalComponent, {data, class: 'modal-dialog-centered'});
    const selectDialog = ref.content as SelectExistingCheckpointModalComponent;
    const selection = await selectDialog.selection;
    if (selection) {
      const checkpointToDuplicate = selection.checkPoint;
      let duplicate = {
        ...checkpointToDuplicate,
        reference: checkpointToDuplicate.label,
        id: null,
      } as TemplateCheckpoint;

      duplicate = await this.checklists.addNewCheckpoint(duplicate).toPromise();
      await this.checklists.addCheckpoint(this.node.id, duplicate.id).toPromise();

    }

  }
}

type DroppedCheckpoint = CdkDragDrop<ChecklistNode, ChecklistNode, ChecklistNodeCheckpoint>;
type DroppedChecklistNode = CdkDragDrop<ChecklistNode, ChecklistNode, ChecklistNode>;
